/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/**
 * @namespace CanvasRenderer
 * @description Namespace containing all classes used to power the TouchPlan canvas-based renderer.
 */
import { Observable, merge, zip, interval } from 'rxjs';
import { map, sample, takeUntil, debounceTime, scan, filter, switchMap } from 'rxjs/operators';
import { ICanvasApplicationInitOptions } from '@appCanvasCommon/interfaces/canvas-renderer.interface';
import { Renderer, utils, Ticker, Assets, Rectangle } from 'pixi.js-legacy';
import { TicketMapper } from '@appPlanApplication/data-mappers';
import { TicketManager } from '@appPlanApplication/tickets/TicketManager';
import { planRendererInfo } from '@appCanvasCommon/core/RendererInfo';
import { APPLICATION_EVENTS } from '@appCanvasCommon/core/ApplicationEvents';
import { LevelOfDetail } from '@appPlanApplication/lod/LevelOfDetail';
import { fontManager } from '@appCanvasCommon/fontpath/FontManager';
import * as HammerWrapper from '@appPlanApplication/HammerWrapper';
import { throttleEvent } from 'utils/basic-utils';
import { moveContainerToNewLayer } from 'utils/ticket-utils';
import { FancyPinHistory } from '@appPlanApplication/pin-history/FancyPinHistory';
import {
	PlanDrag,
	PlanPinch,
	PlanTap,
	SelectionBox,
	SelectionBoxId,
	HoldCircle,
} from '@appPlanApplication/interaction';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';
import { DependencyLines } from '@appPlanApplication/non-tickets/DependencyLines';
import { FancySwimlanes } from '@appPlanApplication/swimlanes/FancySwimlanes';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { RenderDependencies } from '@appPlanApplication/RenderDependencies';
import { Side, Ticket } from '@touchplan/common';
import { clipboardManager } from '../services/ClipboardManager';
import { RenderTime } from '@appCanvasCommon/debug/RenderTime';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { decorateAccessibilityManager } from '../services/AccessibilityManagerDecorator';
import { TicketType } from '@touchplan/common';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { analyticsService } from 'analytics';
import { UserPreferenceStateService } from '../../services/user-preference-state.service';
import { InsertionPoint } from '@appPlanApplication/insertion-point/InsertionPoint';
import { DecoratedRectangle } from '../../app-data/models/DebugRectangles';
import { FancyTimeline } from '@appPlanApplication/timeline/FancyTimeline';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { CloseOnInteraction } from '@appPlanApplication/interaction/components/CloseOnInteraction';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FancyPrint } from '@appPlanApplication/export/FancyPrint';

/**
 * Starts up a new PIXI.WebGLRenderer or PIXI.CanvasRenderer
 * @memberOf CanvasRenderer
 */
export class TicketPlanApplication extends AbstractApplication {
	public preRender$;
	public postRender$;

	public swimlanes: FancySwimlanes;

	/**
	 * Instance of InsertionPoint
	 */
	public insertionPoint: InsertionPoint;

	/**
	 * Components on the RendererApplication (Usually interaction)
	 */
	public components: ComponentManager;
	/**
	 * Instance of TicketMapper
	 */
	public ticketMapper: TicketMapper;
	/**
	 * Instance of TicketManager
	 */
	public ticketManager: TicketManager;
	/**
	 * Instance of a TweenManager. This manages all tweens.
	 */
	public pinHistory: FancyPinHistory;
	/**
	 * Contains a reference of the FancyTimeline, which manages the different "drawers" for the timeline elements
	 */
	public timeline: FancyTimeline;
	/**
	 * Instance of the fancy print exporter for ticket space
	 */
	public export!: FancyPrint | null;
	/**
	 * A single instance of the FancyGraphics object class. Used to draw things in the world.
	 */
	public _graphics: FancyGraphics;
	public uiGraphics: FancyGraphics;
	// public dimmer: FancyDimmer;
	private hammerConnector;
	private preferences$ = this.userPreferenceStateService.preferences$;

	/**
	 * Render performance  that can be enabled with `toggleFpsTicker` via browser console.
	 */
	private fpsText: RenderTime = null;
	private logger: ILoggerService;

	/**
	 * @param canvasAngularLand
	 * @param initOptions
	 */
	constructor(
		public override canvasAngularLand: CanvasAngularLand,
		initOptions: ICanvasApplicationInitOptions,
		private userPreferenceStateService: UserPreferenceStateService
	) {
		super(canvasAngularLand, initOptions);

		this.logger = LoggerServiceFactory.create('TicketPlanApplication');

		RenderDependencies.isDestroyed = false;

		this.components = new ComponentManager(this);

		this.hammerConnector = HammerWrapper.init(this);

		this.preRender$ = new Observable().pipe(takeUntil(this.shutdownSubject$));
		this.postRender$ = new Observable().pipe(takeUntil(this.shutdownSubject$));

		this.initializeFonts();
		this.initializePlanApplication(initOptions);
		this.initializeWatchers();
		this.initializePlanGestures();
		this.initializeDebugRect();

		this._scaleDown();
		this._logGPUInfo();

		planRendererInfo.renderer = this.renderer;

		decorateAccessibilityManager(this.safeRender);
	}

	private initializePlanApplication(initOptions): void {
		const cancel = throttleEvent('resize', 'throttledResize');
		window.addEventListener('throttledResize', this.onResize);

		const depLines = new DependencyLines(
			this.canvasAngularLand,
			this.layerManager.getLayer(LayerPaths.WORLD_ABOVE),
			this.layerManager.getLayer(LayerPaths.WORLD_BACKGROUND),
			this.camera,
			this.renderer,
			this.safeRender,
			this.userPreferenceStateService
		);

		// Create instance of our TicketMapper.
		// This registers an observer on the passed in Observable/Subject.
		// Every time an update is received, it maps the data to a corresponding DataContainer. If no DataContainer exists, it creates one.
		// The TickerMapper exposes a new Observable which fires every time a DataContainer is updated. It passes the DisplayObject representing the DataContainer to all observers.
		// The first parameter is the DisplayObject where new DataContainers will be created.
		const mergedTicketEvents = merge(
			this.canvasAngularLand.tickets.soakedRaw$,
			this.canvasAngularLand.ticketDragService.ghostTickets,
			this.canvasAngularLand.wideTicketSize$.pipe(
				switchMap((val) =>
					this.canvasAngularLand.tickets.replayRawEvent$().pipe(
						filter(
							(t: any) =>
								t.payload.view.side === Side.PULL &&
								(!t.payload.view.type || t.payload.view.type === TicketType.Task)
						),
						takeUntil(this.shutdownSubject$)
					)
				)
			)
		);
		// mergedTicketEvents = empty(); //for disabling ticket rendering

		this.ticketMapper = new TicketMapper(
			this.canvasAngularLand,
			this.layerManager,
			this.layerManager.getLayer(LayerPaths.TICKETS),
			mergedTicketEvents
		);

		// Create instance of Ticket Manager
		// This class is responsible for updating and creating all SpriteTickets and associated them with a DataContainer.
		// We register an Observer to the TicketMapper ticketUpdated$ subject which kicks off the ticket update cycle.

		this.ticketManager = new TicketManager(
			this.canvasAngularLand,
			this.userPreferenceStateService,
			this.layerManager,
			this.camera,
			this.safeRender
		);
		this.ticketManager.listen(this.ticketMapper.emitter, 'update', 'updateData', 'destroyTicket');

		// MOC-3960 - Generic dimmer for the canvas
		// MOC-4146 - They don't want dimmer now
		// this.dimmer = new FancyDimmer(this, this.layerManager.getLayer(LAYER_NAMES.WORLD_ABOVE));

		this.destroyList.push(this.components, this.ticketMapper, this.ticketManager, depLines);
	}

	private initializeFonts(): void {
		// Load the fonts
		// As part of solving TP-5177 we decided to implement a sequential load of fonts with a timeout-retry strategy
		// just to be safe on font-loading in cases where the opentype.js library doesn't have a good handling of font load requests
		fontManager.loadFonts([
			'assets/fonts/Roboto-Bold.ttf',
			'assets/fonts/Roboto-Medium.ttf',
			'assets/fonts/Roboto-Black.ttf',
		]);
	}

	public override initializeLayers(initOptions?: ICanvasApplicationInitOptions): void {
		this._graphics = new FancyGraphics();
		this.uiGraphics = new FancyGraphics();

		// Create our Layers.
		this.layerManager.addLayer(LayerPaths.BACKGROUND_VISUALS, this.stage);
		this.layerManager.addLayer(LayerPaths.BACKGROUND_GESTURES, this.stage);
		this.layerManager.addLayer(LayerPaths.BACKGROUND, this.stage);
		this.layerManager.addLayer(LayerPaths.UI_GRANULAR_TICKETS, this.stage);

		// This adds the world node to the scene graph
		super.initializeLayers();
		this.backgroundGestureContainer.type = 'plan';

		this.layerManager.addLayer(LayerPaths.WORLD_BACKGROUND, this.world);
		this.layerManager.addLayer(LayerPaths.TICKETS, this.world);
		this.layerManager.addLayer(LayerPaths.TICKET_DRAG, this.world);
		this.layerManager.addLayer(LayerPaths.WORLD_ABOVE, this.world);
		this.layerManager.addLayer(LayerPaths.UI, this.stage);
		this.layerManager.addLayer(LayerPaths.BLOCKER, this.stage);

		this.camera.worldGraphics = this._graphics;

		// Setup world graphics instance
		this.layerManager.getLayer(LayerPaths.WORLD_ABOVE).addChild(this._graphics);
		this.layerManager.getLayer(LayerPaths.UI).addChild(this.uiGraphics);
	}

	private initializeWatchers(): void {
		this.preferences$
			.pipe(takeUntil(this.shutdownSubject$), sample(this.canvasAngularLand.renderQueueWithCleanup$))
			.subscribe(() => {
				this.logger.log('Zone.current.name', Zone.current.name);
				this.run();
			});

		// Fires once all PlanApplication dependencies have been loaded
		RenderDependencies.loaded$.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			// HoldCircle
			this.components.add(
				'planHold',
				new HoldCircle(),
				this.canvasAngularLand,
				this.backgroundGestureContainer,
				this.tweenManager,
				this.safeRender
			);

			this.components.add('closeOnInteraction', new CloseOnInteraction(), this.canvasAngularLand);

			// Mark Canvas as loaded
			this.swimlanes = new FancySwimlanes(
				this.canvasAngularLand,
				this.layerManager.getLayer(LayerPaths.BACKGROUND),
				this.layerManager.getLayer(LayerPaths.UI),
				this.camera,
				this
			);
			// Create our PinHistory renderer
			this.pinHistory = new FancyPinHistory(
				this.canvasAngularLand,
				this.camera,
				this.safeRender,
				this.userPreferenceStateService
			);

			this.insertionPoint = new InsertionPoint(
				this.canvasAngularLand,
				this.layerManager.getLayer(LayerPaths.UI),
				this.camera,
				this
			);

			this.timeline = new FancyTimeline(this.canvasAngularLand, this);
			this.handleFeatureFlags();

			clipboardManager.onApplicationSetup(this, this.layerManager.getLayer(LayerPaths.BACKGROUND));

			this.destroyList.push(this.pinHistory, this.timeline);

			// Dispatch the LOD
			LevelOfDetail.update(this.camera);
			LevelOfDetail.forceUpdate();
		});

		// Handles first draw
		const unsubFirstDraw = this.ticketMapper.firstDraw$.subscribe(() => {
			RenderDependencies.firstDraw = true;
			this.run();
			unsubFirstDraw.unsubscribe();
		});

		// Performs redraws when ticketManager updates
		// TP-546: The TicketManager sends an update when the renderer needs to do a re-draw. So we need to queue up another
		// update and draw cycle with the renderQueue.
		this.ticketManager.update$.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => this.run());

		// Ticket data changed. I don't like this. Fix later.
		this.ticketMapper.emitter.on('draw', () => {
			this.ticketManager.update$.next();
		});

		// For window.perf logging
		const evt$ = merge(
			this.preRender$.pipe(
				map((count) => ({ name: 'prerender', extraData: { logSettle: true, spammy: true } }))
			),
			this.postRender$.pipe(
				map((count) => ({
					name: 'postrender',
					extraData: { logSettle: true, previousEventName: 'prerender', spammy: true },
				}))
			)
		);

		window.perf.merge(evt$);
	}

	/**
	 * Test code. Remove me. Probably. Maybe.
	 */
	public initializePlanGestures() {
		this.components.add('planTap', new PlanTap(), this.canvasAngularLand, this.backgroundGestureContainer);
		this.components.add('planDrag', new PlanDrag(), this.canvasAngularLand, this.backgroundGestureContainer);
		this.components.add(
			'planPinch',
			new PlanPinch(),
			this.canvasAngularLand,
			this.backgroundGestureContainer
		);
		this.components.add(
			SelectionBoxId.toString(),
			new SelectionBox(),
			this.canvasAngularLand,
			this.backgroundGestureContainer
		);
	}

	private initializeDebugRect(): void {
		const debugRectContainer = new FancyGraphics();
		let added = false;

		this.canvasAngularLand.debugRectangles.listener$.subscribe(
			(rectList: Map<string, DecoratedRectangle>) => {
				// this.logger.log('hi', rectList);
				if (rectList.size > 0) {
					if (!added) {
						added = true;
						this.world.addChild(debugRectContainer);
					}
				} else {
					if (added) {
						added = false;
						this.world.removeChild(debugRectContainer);
					}
				}

				debugRectContainer.clear();
				rectList.forEach((rect) => {
					debugRectContainer.beginFill(rect.color, 0.3);
					debugRectContainer.drawRect(rect.x, rect.y, rect.width, rect.height);
				});
			}
		);

		this.world.addChild(debugRectContainer);
	}

	private _logGPUInfo(): void {
		if (this.rendererInfo.isWebGL()) {
			const gl: WebGLRenderingContext = (<Renderer>this.renderer).gl;
			const dbgInfo = gl.getExtension('WEBGL_debug_renderer_info');
			if (dbgInfo) {
				const vendor = gl.getParameter(dbgInfo.UNMASKED_VENDOR_WEBGL);
				const renderer = gl.getParameter(dbgInfo.UNMASKED_RENDERER_WEBGL);
				this.logger.log('WebGL VENDOR:', vendor);
				this.logger.log('WebGL RENDERER:', renderer);
				analyticsService.identify(undefined, {
					webglVendor: vendor,
					webglRenderer: renderer,
				});
			}
		}
	}

	/**
	 * Called before an update loop
	 */
	public preUpdate = (delta: number, forceDirty: boolean = false) => {
		if (!this.renderer) {
			return;
		}
		this.camera.preUpdate(delta);
		this.emit(APPLICATION_EVENTS.PRE_UPDATE);
	};
	/**
	 * Calls a single iteration of the update loop. This calls the 'update' function on each ticket and each ticket component.
	 * Useful when you need to run logic every tick update.
	 */
	public override update = (delta: number, forceDirty: boolean = false) => {
		if (!this.camera) {
			return;
		}
		// let a = performance.now();
		this.camera.update(delta);
		LevelOfDetail.update(this.camera);
		if (!this.renderer) {
			return;
		}
		this.components.update(forceDirty);
		this.ticketMapper.update(forceDirty);
		if (this.ticketManager.ticketCollision) {
			this.ticketManager.ticketCollision.update();
		}
		if (this.pinHistory) {
			this.pinHistory.update();
		}
		if (this.timeline) {
			this.timeline.update(delta);
		}
		this.emit(APPLICATION_EVENTS.UPDATE);
	};
	/**
	 * Called after an update loop
	 */
	public postUpdate = (delta: number, forceDirty: boolean = false) => {
		if (!this.renderer) {
			return;
		}
		this.emit(APPLICATION_EVENTS.POST_UPDATE);
	};

	public safeUpdate = (forceDirty: boolean = false) => {
		renderQueue.push((delta) => {
			this.preUpdate(delta, forceDirty);
			this.update(delta, forceDirty);
			this.postUpdate(delta, forceDirty);
		}, 'update');
	};

	/** Shorthard for pushing render onto the renderQueue */
	public safeRender = () => {
		this.markAsDirty();
	};
	/**
	 * Helper method to render a single display object, or the entire scene
	 */
	public render = () => {
		this.draw();
	};

	private _scaleDown(): void {
		this.view.style.width = this.renderer.width / this.renderer.resolution + 'px';
		this.view.style.height = this.renderer.height / this.renderer.resolution + 'px';
	}

	// MOC-2949
	public printResize(w: number, h: number): void {
		this.renderer.resize(w, h);
		this._scaleDown();
	}

	public onResize = (event): TicketPlanApplication => {
		if (!this.canvasAngularLand || !this.canvasAngularLand.hud) {
			return;
		}
		const w = this.canvasAngularLand.hud.w;
		const h = this.canvasAngularLand.hud.h;
		this.renderer.resize(w, h);
		this.camera.resize(w, h);
		this.backgroundGestureContainer.hitArea = new Rectangle(
			0,
			0,
			this.renderer.view.width,
			this.renderer.view.height
		);
		// BEGIN PLACEHOLDER UNTIL TP-6818 IS FIXED
		// TP-7160: The FancyTimeline is only created when the texture atlas is fully loaded because it needs access to
		// texture data in order to render the timeline elements. This means there is a small window of time when you can
		// resize the browser window and have an error occur because the `TicketPlanApplication` was created, but the underlying
		// texture atlas was not yet loaded, meaning the FancyTimeline was not yet created.
		// So it tries to invokes `resize()` on a field that has an undefined value.
		if (this.timeline) {
			this.timeline.resize(w, h);
		}
		// END PLACEHOLDER
		this._scaleDown();
		this.safeRender();
		return this;
	};
	/**
	 * Ticks the renderer and calls preUpdate, update, postUpdate and render functions.
	 */
	public run = (forceDirty: boolean = false) => {
		this.safeUpdate(forceDirty);
		this.safeRender();
	};

	/**
	 * Called when the scene graph should be destroyed. This happens when navigating away from the plan view page usually. This does the following:
	 * 1. Destroys stage, renderer and nulls all references
	 * 2. Gets the textureCache, iterates the list and calls .destroy() on each texture
	 * 3. Gets the baseTextureCache, iterates the list and calls .destroy() on each BaseTexture
	 */
	public override destroy() {
		// BEGIN PLACEHOLDER: Replace as part of TP-6850
		super.destroy(DestroyOptions.sourceOnly);
		// END PLACEHOLDER

		if (this.swimlanes) {
			this.swimlanes.destroy(DestroyOptions.includeChildren);
		}

		if (this.insertionPoint) {
			this.insertionPoint.destroy();
		}

		if (this.export) {
			this.export.destroy(DestroyOptions.includeChildren);
		}

		this.emit(APPLICATION_EVENTS.DESTROY);

		this.hammerConnector.destroy();
		this.destroyTextureCacheAsync();

		// Remove resize listener
		window.removeEventListener('throttledResize', this.onResize);

		LevelOfDetail.emitter.removeAllListeners();

		LevelOfDetail.reset();
		RenderDependencies.isDestroyed = true;
		RenderDependencies.isLoaded = false;

		this.canvasAngularLand = null;
		planRendererInfo.renderer = null;
	}

	private getTicketContainerFromTicket(editTicket: Ticket): FancyContainer {
		const ticketMapper = this.ticketMapper;
		return ticketMapper.mapping.get(editTicket.$id);
	}

	/** immediately clears the cache and creates a 5ms queue that destroys the textures later on */
	private destroyTextureCacheAsync() {
		const textureCache = Object.assign({}, utils.TextureCache);
		const baseTexture = Object.assign({}, utils.BaseTextureCache);
		Assets.reset();

		utils.clearTextureCache();

		const texture$ = new Observable((subscriber) => {
			for (const key in textureCache) {
				subscriber.next(textureCache[key]);
			}
			for (const key in baseTexture) {
				subscriber.next(baseTexture[key]);
			}
			subscriber.complete();
		});
		const tick$ = interval(5);
		zip(texture$, tick$)
			.pipe(map(([tex, tick]) => tex))
			.subscribe({
				// next: x => this.logger.log('got value ' + x),
				// error: err => this.logger.error('something wrong occurred: ' + err),
				complete: () => {
					done();
				},
			});
		function done() {
			window.perf.log('end base texture destruction', {
				previousEventName: 'start base texture destruction',
			});
		}
		window.perf.log('start texture destruction');
	}

	public toggleFpsTicker() {
		if (this.fpsText === null) {
			this.fpsText = new RenderTime('render time', null, this.stage);
			this.fpsText.zIndex = 9999;
			this.fpsText.visible = false;
		}
		this.fpsText.visible = !this.fpsText.visible;
		if (this.fpsText.visible) {
			this.fpsText.prepare();
			Ticker.shared.add(this._drawWithFps);
		} else {
			Ticker.shared.remove(this._drawWithFps);
		}
	}

	/**
	 * Helper method to handle subscription creation for all future feature flag items that will live
	 * off of the main TicketPlanApplication class. This is called after the canvas textures are loaded.
	 * @private
	 */
	private handleFeatureFlags(): void {
		// TP-7260: Plan Printing Feature Flag handling
		this.canvasAngularLand.featureFlagManager
			.getFlag$(DynamicFlagTypes.PLAN_PRINT_ENABLED)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((isEnabled: boolean) => {
				if (!isEnabled) {
					this.export?.destroy(DestroyOptions.includeChildren);
					this.export = null;
					return;
				}

				// Destroy previous instance, if it exists. Then create a new one
				this.export?.destroy(DestroyOptions.includeChildren);
				this.export = new FancyPrint(this.canvasAngularLand, this);
			});
	}

	private _drawWithFps = (time: number) => {
		this.fpsText.onStartFrame();

		this.preUpdate(time);
		this.fpsText.onPreUpdate();

		this.update(time);
		this.fpsText.onUpdate();

		this.postUpdate(time);
		this.fpsText.onPostUpdate();

		this.draw();
		this.fpsText.onEndFrame();
	};
}
